import { COLOR_CONSTANTS } from "./styles";

/** 2D·3D 지도 위 이슈 도구 툴바 버튼의 공용 스타일 토큰 (annotation-tool-interaction 스펙). 버튼 칸은 라운드 없이 활성 배경이 가득 채우고, 라운드는 그룹 컨테이너만 가진다. */
export const MAP_TOOL_BUTTON = {
  SIZE_PX: 36,
  ICON_PX: 22,
  GROUP_RADIUS_PX: 10,
  GROUP_GAP_PX: 10,
  GROUP_TITLE_FONT_PX: 11,
  GROUP_TITLE_HEIGHT_PX: 24,
  /** 그룹 제목 타이포 — 2D `ControlGroup`(emotion)과 3D `Toolbar`·`EtcToolbar`(sx)가 같은 값을 읽는다. 줄높이는 MUI 기본(1.5)이면 줄박스가 커져 제목 칸에서 가운데가 어긋난다 */
  GROUP_TITLE_FONT_WEIGHT: 500,
  GROUP_TITLE_LINE_HEIGHT: 1,
  GROUP_TITLE_LETTER_SPACING: "0.5px",
  TOOLTIP_OFFSET_PX: 12,
  /** 툴팁 기하 — 2D `Button`·3D `ToolButton` 이 같은 값을 읽는다(높이는 세로 가운데 정렬이라 패딩 대신 고정) */
  TOOLTIP_HEIGHT_PX: 28,
  TOOLTIP_PADDING_X_PX: 12,
  TOOLTIP_RADIUS_PX: 4,
  TOOLTIP_FONT_PX: 12,
  /** 툴팁 표면 — 2D `Button` 의 emotion 규칙과 3D `MAP_TOOL_TOOLTIP_SX` 가 같은 값을 읽는다 */
  TOOLTIP_FONT_WEIGHT: 500,
  TOOLTIP_OPACITY: 0.9,
  TOOLTIP_SHADOW: "0 2px 8px rgba(0, 0, 0, 0.15)",
  TOOLTIP_Z_INDEX: 1000,
  /** 꼬리 삼각형 한 변 — 세로 절반 높이이자 가로 길이 */
  TOOLTIP_ARROW_PX: 6,
  DISABLED_OPACITY: 0.4,
  activeBackground: COLOR_CONSTANTS.primary.main,
  activeHoverBackground: COLOR_CONSTANTS.primary.dark,
  activeIcon: COLOR_CONSTANTS.common.white_states.main,
  idleIcon: COLOR_CONSTANTS.text.secondary,
  hoverBackground: COLOR_CONSTANTS.action.hover,
  groupBackground: COLOR_CONSTANTS.common.white_states.main,
  groupTitleColor: COLOR_CONSTANTS.text.secondary,
  groupTitleBorder: COLOR_CONSTANTS.COLOR.GREY[300],
  groupShadow: "0 2px 10px rgba(0, 0, 0, 0.16)",
  tooltipBackground: "#2e3642",
  tooltipColor: COLOR_CONSTANTS.common.white_states.main,
} as const;

/** 2D↔3D 전환 시 도구가 움직여 보이지 않도록 두 지도가 공유하는 좌상단 좌표. 2D 는 ControlSection 이 이 값을 직접 읽는다. */
export const MAP_TOOL_LAYOUT = {
  LEFT_PX: 10,
  FULLSCREEN_TOP_PX: 12,
  /** 전체화면 그룹(36px)과 도구 툴바 사이 세로 간격 — 12 + 36 + 12 = TOOLBAR_TOP_PX */
  SECTION_GAP_PX: 12,
  TOOLBAR_TOP_PX: 60,
} as const;

export const DRAWING_TOOL_CURSOR = "crosshair";

/**
 * 2D 그리기 버튼 6종이 도구를 끌 때 `map.getViewport().style.cursor` 에 쓰는 값
 * (점·글자·사각형은 `releaseDrawToolUi` 를 거치고, 다중 점·선·면은 자체 해제 경로에서 직접 쓴다).
 *
 * 주의 — 이 값은 **도구를 한 번 끈 뒤의 지도 상시 커서**가 된다: `.ol-viewport` 는
 * `useHoverCursor` 가 쓰는 `map.getTargetElement()` 의 자식이자 그 안을 100% 채우므로,
 * 여기서 남긴 뷰포트 인라인 커서가 훅의 `"default"`/`"pointer"` 를 덮어 훅의 쓰기가 지도
 * 표면에 닿지 않는다. 바닥 커서를 hover 훅에 되돌리려면 `""`(인라인 해제)를 써야 한다.
 * 즉 값 변경은 이 상수를 읽는 6경로 전체에서 관찰 가능한 동작 변화다.
 * 3D 호스트는 이 상수를 읽지 않는다 — `Inspection3d/index.tsx` 가 같은 값을 리터럴로 정한다.
 */
export const DRAW_TOOL_EXIT_CURSOR = "pointer";

/** 완료 뒤에도 잠깐은 그리기 상태로 둔다 — `isDrawing` 을 보는 지도 클릭 소비자(useFeatureClickSelection·useSelectAnnotation)가 완료 클릭을 선택 클릭으로 처리하지 않도록 */
export const DRAWING_FLAG_RELEASE_DELAY_MS = 100;

/** 버튼 칸 안 아이콘이 차지하는 상자 — 아이콘 리소스 크기와 무관하게 칸 안에서 같은 자리를 차지한다 */
export const MAP_TOOL_ICON_BOX = {
  width: MAP_TOOL_BUTTON.ICON_PX,
  height: MAP_TOOL_BUTTON.ICON_PX,
} as const;

/** 전체화면 토글의 상태별 라벨 키·아이콘 — 2D `FullScreenFeature` 와 3D `FullScreenButton` 이 같은 표를 읽어 두 지도의 버튼이 함께 바뀐다 */
export const FULLSCREEN_TOGGLE = {
  labelKey: {
    enter: "mapTool.tool.fullscreen",
    exit: "mapTool.tool.exitFullscreen",
  },
  icon: {
    enter: "24/tool/basic/expand",
    exit: "etc/exit screen",
  },
} as const;

/** 2D·3D 양쪽에 있는 도구 버튼의 툴팁 i18n 키(`timeSeries` 네임스페이스) — 같은 도구는 두 지도에서 같은 문구를 읽는다. 3D 는 `ANNOTATION_TOOL_LABEL_KEYS`·`ETC_TOOL_LABEL_KEYS` 가 이 표에서 파생된다. 한쪽에만 있는 도구(2D 다중 점, 3D 측정)의 키는 여기 없다 */
export const MAP_TOOL_LABEL_KEYS = {
  marker: "mapTool.tool.marker",
  line: "mapTool.tool.line",
  rectangle: "mapTool.tool.rectangle",
  polygon: "mapTool.tool.polygon",
  text: "mapTool.tool.text",
  droneImage: "mapTool.tool.droneImage",
  annotationTitle: "mapTool.tool.annotationTitle",
} as const;

/** 드론 이미지 찾기 도구의 라벨 키·아이콘 — 2D `DroneImagePicker` 와 3D `ETC_TOOL_ICONS`·`ETC_TOOL_LABEL_KEYS` 가 같은 표를 읽어 두 지도의 버튼이 함께 바뀐다 */
export const DRONE_IMAGE_TOOL = {
  labelKey: MAP_TOOL_LABEL_KEYS.droneImage,
  icon: "etc/camera-line",
} as const;

/**
 * '이슈 이름 표시' 토글의 라벨 키·상태별 아이콘 — 2D `TimeSeriesPage` 의 지도 도구 버튼과 3D `AnnotationLabelToggle` 이
 * 같은 표를 읽어 두 지도의 버튼이 함께 바뀐다. 아이콘 키는 **지금 이름이 보이는지**로 고른다:
 * `shown`(보이는 중 — 누르면 숨긴다) · `hidden`(숨긴 중 — 누르면 드러낸다).
 * 아이콘 크기·색은 이 표에 없다 — 두 소비처가 각자 정하며 지금은 서로 다르다.
 */
export const ANNOTATION_TITLE_TOGGLE = {
  labelKey: MAP_TOOL_LABEL_KEYS.annotationTitle,
  icon: {
    shown: "etc/eye-off-line",
    hidden: "etc/eye-line",
  },
} as const;

/** 툴바 한 칸을 그대로 채우는 버튼의 공통 sx — 3D `ToolButton`·`AnnotationLabelToggle`·`BackgroundColorToggle` 이 읽는다(2D `Button` 은 emotion css 라 같은 토큰을 직접 읽는다). 라운드는 그룹 컨테이너가 마지막 칸에만 덧씌운다 */
export const MAP_TOOL_BUTTON_CELL_SX = {
  width: MAP_TOOL_BUTTON.SIZE_PX,
  height: MAP_TOOL_BUTTON.SIZE_PX,
  borderRadius: 0,
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: MAP_TOOL_BUTTON.hoverBackground,
  },
} as const;
